import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../shared/services/user.service';
import { UserSettings, Tenant, DEFAULT_INVOICE_PROMPT } from '../../../shared/models/user.model';
import { GoogleCalendarService } from '../../../shared/services/google-calendar.service';
import { TenantService } from '../../../shared/services/tenant.service';


@Component({
  selector: 'app-settings-view',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './settings-view.html'
})
export class SettingsView implements OnInit {
  private userService = inject(UserService);
  private tenantService = inject(TenantService);
  readonly googleCalendar = inject(GoogleCalendarService);
  googleCalendarError = '';
  activeSettingsTab = signal<'preferences' | 'integrations'>('preferences');

  tenants: Tenant[] = [];

  settings: UserSettings | null = null;
  formSettings: Partial<UserSettings> = {
    secondsToAvoid: "5",
    aiInvoicePrompt: DEFAULT_INVOICE_PROMPT,
    displayModeForProject: "LIST",
    defaultTenantId: '',
  };
  loadingSettings = false;

  ngOnInit() {
    this.userService.settings$.subscribe(settings => {
      this.settings = settings;
      const savedPrompt = settings?.aiInvoicePrompt;
      this.formSettings = {
        secondsToAvoid: settings?.secondsToAvoid ?? '5',
        aiInvoicePrompt: (savedPrompt && savedPrompt.trim().length > 0) ? savedPrompt : DEFAULT_INVOICE_PROMPT,
        displayModeForProject: settings?.displayModeForProject ?? "LIST",
        defaultTenantId: settings?.defaultTenantId ?? '',
      };
      if (settings?.id) this.googleCalendar.init(settings.id);
    });
    // Load tenants: admins see all, non-admins see only their own
    this.userService.currentUser$.subscribe(user => {
      if (user?.role === 'SUPERADMIN' || user?.role === 'ADMIN') {
        // Admins: load all tenants
        this.tenantService.tenants$.subscribe(tenants => {
          this.tenants = tenants;
        });
        this.tenantService.init();
      } else if (user?.tenants) {
        // Regular users: only their assigned tenants
        this.tenants = user.tenants
          .filter(ut => ut.tenant)
          .map(ut => ut.tenant as Tenant);
      }
    });
  }

  async saveSettings() {
    this.loadingSettings = true;
    try {
      let promptToSave = this.formSettings.aiInvoicePrompt;
      if (!promptToSave || promptToSave.trim() === '') {
        promptToSave = DEFAULT_INVOICE_PROMPT;
        this.formSettings.aiInvoicePrompt = DEFAULT_INVOICE_PROMPT;
      }
      await this.userService.updateUserSettings({
        secondsToAvoid: String(this.formSettings.secondsToAvoid),
        aiInvoicePrompt: promptToSave,
        displayModeForProject: this.formSettings.displayModeForProject,
        defaultTenantId: this.formSettings.defaultTenantId || '',
      });
      alert("Settings saved");
    } catch (e) {
      console.error(e);
      alert("Error settings");
    } finally {
      this.loadingSettings = false;
    }
  }

  resetAiPrompt() {
    this.formSettings.aiInvoicePrompt = DEFAULT_INVOICE_PROMPT;
  }

  async connectGoogleCalendar() {
    this.googleCalendarError = '';
    try {
      await this.googleCalendar.connect();
      // Load calendar list right after connecting
      await this.refreshCalendarList();
    } catch (e: any) {
      this.googleCalendarError = e?.message ?? 'Connection failed';
    }
  }

  async disconnectGoogleCalendar() {
    await this.googleCalendar.disconnect();
  }

  toggleDemoMode() {
    if (this.googleCalendar.isDemoMode()) {
      this.googleCalendar.disableDemoMode();
    } else {
      this.googleCalendar.enableDemoMode();
    }
  }

  // ─── Calendar list management ────────────────────────────────────────────
  loadingCalendars = false;

  async refreshCalendarList() {
    this.loadingCalendars = true;
    try {
      await this.googleCalendar.listCalendars();
    } finally {
      this.loadingCalendars = false;
    }
  }

  isCalendarEnabled(calId: string): boolean {
    const enabled = this.googleCalendar.enabledCalendarIds();
    return enabled === null || enabled.includes(calId);
  }

  async toggleCalendar(calId: string, checked: boolean) {
    const all = this.googleCalendar.calendarList().map(c => c.id);
    let current = this.googleCalendar.enabledCalendarIds() ?? all;
    const next = checked
      ? [...new Set([...current, calId])]
      : current.filter(id => id !== calId);
    // If all are enabled, store null (meaning all) to keep it clean
    await this.googleCalendar.saveEnabledCalendars(next.length === all.length ? null : next);
  }

  async selectAllCalendars() {
    await this.googleCalendar.saveEnabledCalendars(null);
  }

  async selectNoCalendars() {
    await this.googleCalendar.saveEnabledCalendars([]);
  }
}

